import React, { useMemo, useState } from 'react';
import { PhoneIcon } from 'lucide-react';
import { galleryAssets, altFor, type AssetCategory } from '../../data/assets';
import { phones } from '../../data/siteConfig';
import type { Lang } from '../../types/funnel';
type Filter = 'all' | AssetCategory;
const labels: Record<Lang, Record<Filter, string>> = {
  en: {
    all: 'All',
    trucks: 'Trucks',
    packing: 'Protection',
    loading: 'Loading',
    access: 'Stairs & access',
    moving: 'Moving day',
    team: 'Team'
  },
  es: {
    all: 'Todas',
    trucks: 'Camiones',
    packing: 'Protección',
    loading: 'Carga',
    access: 'Escaleras y acceso',
    moving: 'Día de mudanza',
    team: 'Equipo'
  }
};
const copy = {
  en: {
    title: 'Real work by our crew',
    sub: 'Protection, loading and handling from past jobs. Locations aren’t specified.',
    more: 'Show more photos',
    note: 'Some uniforms in older photos show a previous phone number. To reach us today, call'
  },
  es: {
    title: 'Trabajo real de nuestro equipo',
    sub: 'Protección, carga y manejo en trabajos anteriores. No se especifican ubicaciones.',
    more: 'Ver más fotos',
    note: 'Algunos uniformes en fotos anteriores muestran un número previo. Para comunicarse hoy, llame al'
  }
};
const PAGE = 8;
export function PhotoGallery({
  lang
}: {
  lang: Lang;
}) {
  const all = useMemo(() => galleryAssets(), []);
  const [filter, setFilter] = useState<Filter>('all');
  const [shown, setShown] = useState(PAGE);
  const cats = useMemo(() => ['all', ...Array.from(new Set(all.map(a => a.category)))] as Filter[], [all]);
  const list = filter === 'all' ? all : all.filter(a => a.category === filter);
  const c = copy[lang];
  if (all.length === 0) return null;
  return <section className="border-t border-ink-200 bg-paper" aria-labelledby="gallery-title" data-id="element-537" data-mp="1dd5n7tggtp6z">
      <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6" data-id="element-538" data-mp="2tr5j8tc6wuoy">
        <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-539" data-mp="2tti176hy7520">
          <div data-id="element-540" data-mp="2os2e78w7mogz">
            <h2 id="gallery-title" className="font-display text-2xl font-black text-ink" data-id="element-541" data-mp="2ouolr8j3p3z7">
              {c.title}
            </h2>
            <p className="mt-1 text-[15px] text-ink-600" data-id="element-542" data-mp="1re0qdbyoxz9r">{c.sub}</p>
          </div>
          <div role="tablist" aria-label={c.title} className="-mx-4 flex gap-2 overflow-x-auto px-4 sm:mx-0 sm:px-0" data-id="element-543" data-mp="1vfunxbdlan5p">
            {cats.map(f => <button key={f} type="button" role="tab" aria-selected={filter === f} onClick={() => {
            setFilter(f);
            setShown(PAGE);
          }} className={`min-h-[40px] shrink-0 whitespace-nowrap rounded-full border-2 px-4 text-sm font-semibold transition-[background-color,border-color] duration-150 ease-out ${filter === f ? 'border-ink bg-ink text-white' : 'border-ink-200 bg-white text-ink hover:border-ink-400'}`} data-id="element-544" data-mp="20dntq3azdf5g">
                {labels[lang][f]}
              </button>)}
          </div>
        </div>

        <ul className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-id="element-545" data-mp="hfr8fn68l3xb">
          {list.slice(0, shown).map(a => <li key={a.id} className="overflow-hidden rounded-xl bg-ink-100" data-id="element-546" data-mp="cfdbp1a35gel">
              <img src={a.url!} alt={altFor(a, 'Moving work by our crew')} width={a.w} height={a.h} loading="lazy" decoding="async" style={{
            objectPosition: a.focus ?? 'center'
          }} className="aspect-[3/4] w-full object-cover" data-id="element-547" data-mp="7ga53cegj24i" />
            </li>)}
        </ul>

        {list.length > shown && <div className="mt-6 flex justify-center" data-id="element-548" data-mp="3fyex2rk4qkd3">
            <button type="button" onClick={() => setShown(s => s + PAGE)} className="h-12 rounded-xl border-2 border-ink-200 bg-white px-5 font-semibold text-ink hover:border-ink" data-id="element-549" data-mp="3fzpxqiia3bzz">
              {c.more} ({list.length - shown})
            </button>
          </div>}

        <p className="mt-6 flex flex-wrap items-center gap-x-2 text-sm text-ink-600" data-id="element-550" data-mp="1avdaqsk1e8zk">
          {c.note}
          <a href={phones.ai.href} className="inline-flex min-h-[44px] items-center gap-1.5 font-bold text-ink underline decoration-lime decoration-2 underline-offset-4" data-id="element-551" data-mp="1ev5pjn00xbdd">
            <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-552" data-mp="162k6s6we74oj" />
            {phones.ai.display}
          </a>
        </p>
      </div>
    </section>;
}